import { OnboardingLanguage } from '../../../translations';

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

type QuizByLanguage = Record<OnboardingLanguage, QuizQuestion[]>;

export const journey1Quiz: QuizByLanguage = {
  en: [
    // Q1 — Phase 1: Welcome & Platform Overview
    {
      question: 'What are the two repositories a developer works with on the IDP?',
      options: [
        'Application repo (code) and GitOps repo (configuration per environment)',
        'Application repo (code) and Dockerfile repo',
        'GitOps repo (configuration) and Helm Chart repo',
        'Azure DevOps repo and ArgoCD repo',
      ],
      correctIndex: 0,
      explanation:
        'Step 3 of Phase 1 (How it all connects) explains that the dev works with two repos: the Application repo (code on GitHub) and the GitOps repo (configuration for how the app runs in each environment: variables, secrets, CPU/memory). Confusing the two causes deploy errors.',
    },
    // Q2 — Phase 1: Welcome & Platform Overview
    {
      question: 'Who is responsible for creating and maintaining Dockerfiles?',
      options: [
        'Each development team creates their own Dockerfile',
        'The Tech Lead creates them during environment promotion',
        'The Platform team creates and maintains all Dockerfiles',
        'Dockerfiles are automatically generated by ArgoCD',
      ],
      correctIndex: 2,
      explanation:
        "Step 3 of Phase 1 has a callout: 'The Dockerfile is NOT the dev's responsibility, the Platform team creates and maintains it. Even if you create one, it won't be used.' A dev who creates a Dockerfile wastes time because the pipeline ignores it.",
    },
    // Q3 — Phase 2: Navigating Backstage
    {
      question: 'What happens when a Developer clicks the CREATE COMPONENT button?',
      options: [
        'A form opens to create a new microservice',
        'It opens the Templates page with scaffold options',
        'It shows an error page because Developers have read-only access',
        'It redirects to Azure DevOps to create a new repository',
      ],
      correctIndex: 2,
      explanation:
        "Step 2 of Phase 2 (Quick actions) says: 'CREATE COMPONENT: Visible to everyone, but clicking it as a Developer shows an error page. This is expected, not a bug.' Without knowing this, the dev opens an unnecessary ticket.",
    },
    // Q4 — Phase 2: Navigating Backstage
    {
      question: 'What are the three main Kinds you use daily in the Catalog?',
      options: [
        'Application, Database, and Pipeline',
        'Service, Endpoint, and Domain',
        'Component, Resource, and System',
        'Repository, Namespace, and Cluster',
      ],
      correctIndex: 2,
      explanation:
        'Step 5 of Phase 2 (Understanding kinds) lists: Component (applications: microservices, webapps, libs, frontends), Resource (infra: Storage Accounts, KeyVaults, Redis, CosmosDB), and System (groups all Components and Resources of the project). Without this, the dev cannot navigate the Catalog.',
    },
    // Q5 — Phase 2: Navigating Backstage
    {
      question: "What is the Relations Graph on the System page for?",
      options: [
        'It shows the deploy history of the project',
        'It displays resource consumption (CPU/memory) of each component',
        'It shows how everything in the project connects: which Components depend on which Resources and how services relate to each other',
        'It lists the users who have access to the System',
      ],
      correctIndex: 2,
      explanation:
        "Step 3 of Phase 3 (Relations graph) explains that the Relations card shows how everything in the project connects: which Components depend on which Resources and how services relate to each other. Clicking 'View graph' opens the full view. Essential for understanding dependencies and assessing the impact of changes.",
    },
    // Q6 — Phase 3: Finding Your Project
    {
      question: 'How do you find your project in the Catalog?',
      options: [
        'Search the project name only in the global Search bar',
        "Go to Catalog, change Kind to 'System', locate the project by name",
        "Click 'Domains' in the left sidebar menu",
        'Open ArgoCD and navigate to the application',
      ],
      correctIndex: 1,
      explanation:
        "Step 1 of Phase 3 (Find your system) says: 'To find it: go to the Catalog, change Kind to System, locate your project by name or use the search field, and click to open.' This is the first thing the dev does on the platform.",
    },
    // Q7 — Phase 4: External Tools Overview
    {
      question: 'Which label filter in ArgoCD shows development environment applications?',
      options: [
        'env=dev',
        'env=dv',
        'environment=development',
        'stage=dv',
      ],
      correctIndex: 1,
      explanation:
        "Step 1 of Phase 4 (ArgoCD) lists the label filters: 'env=dv for Development, env=ts for Test, env=pr for Production.' The dev uses this every day to find their applications.",
    },
    // Q8 — Phase 4: External Tools Overview
    {
      question: 'What are the three stages of the Azure DevOps pipeline, in the correct order?',
      options: [
        'Build → Test → Deploy to Kubernetes',
        'Lint → Compile → Push to ArgoCD',
        'Tests → Build and Push Image → Update GitOps (bom.yaml)',
        'Compile → Package → Deploy via Helm',
      ],
      correctIndex: 2,
      explanation:
        "Step 3 of Phase 4 (GitHub & Azure DevOps) describes the three pipeline stages: '1. Tests, 2. Build and Push Image, 3. Update GitOps (bom.yaml).' The pipeline updates the bom.yaml in the GitOps repo, and ArgoCD detects the change and auto-deploys. Understanding this flow is essential for debugging pipeline failures.",
    },
    // Q9 — Phase 5: Permissions & Security Rules
    {
      question: 'Which of the following CAN a Developer do on the IDP?',
      options: [
        'Create new Resources via Backstage templates',
        'Promote environments from dev to production',
        'Browse the entire Catalog and access ArgoCD to view deploy status',
        'Manage secrets directly in KeyVault',
      ],
      correctIndex: 2,
      explanation:
        "Step 1 of Phase 5 (Your permissions as developer) shows in the 'You CAN' column: 'Browse the entire Catalog' and 'Access ArgoCD for deploy status.' The other options are in the 'You CANNOT' column: creating Resources, promoting environments, and managing KeyVault are exclusive to the Tech Lead.",
    },
    // Q10 — Phase 5: Permissions & Security Rules
    {
      question: 'Which of the following practices is strictly PROHIBITED according to security rules?',
      options: [
        'Using Builder mode in Grafana to query logs',
        'Pushing code to the develop branch',
        'Storing code or Nestlé data outside Azure DevOps/GitHub',
        'Accessing ArgoCD to check deploy status',
      ],
      correctIndex: 2,
      explanation:
        "Step 2 of Phase 5 (Security rules) lists as 'Prohibited': 'Code outside Azure DevOps/GitHub.' Also prohibited: Azure Portal access, hardcoded secrets in code, external cloud resources, and public ChatGPT with Nestlé data. Violations trigger automatic alerts and may result in access revocation.",
    },
  ],

  'pt-BR': [
    // Q1 — Fase 1: Welcome & Platform Overview
    {
      question: 'Quais são os dois repositórios com os quais o desenvolvedor trabalha no IDP?',
      options: [
        'Repositório da aplicação (código) e repositório GitOps (configuração por ambiente)',
        'Repositório da aplicação (código) e repositório de Dockerfiles',
        'Repositório GitOps (configuração) e repositório do Helm Chart',
        'Repositório do Azure DevOps e repositório do ArgoCD',
      ],
      correctIndex: 0,
      explanation:
        'O Step 3 da Fase 1 (How it all connects) explica que o dev trabalha com dois repos: o Application repo (código no GitHub) e o GitOps repo (configuração de como a aplicação roda em cada ambiente: variáveis, secrets, CPU/memória). Confundir os dois causa erros de deploy.',
    },
    // Q2 — Fase 1: Welcome & Platform Overview
    {
      question: 'Quem é responsável por criar e manter os Dockerfiles?',
      options: [
        'Cada time de desenvolvimento cria o seu próprio Dockerfile',
        'O Tech Lead cria durante a promoção de ambientes',
        'O time de Plataforma cria e mantém todos os Dockerfiles',
        'Os Dockerfiles são gerados automaticamente pelo ArgoCD',
      ],
      correctIndex: 2,
      explanation:
        "O Step 3 da Fase 1 tem um callout: 'O Dockerfile NÃO é responsabilidade do dev, o time de Plataforma cria e mantém. Mesmo que você crie um, ele não será usado.' Dev que cria Dockerfile perde tempo porque o pipeline ignora.",
    },
    // Q3 — Fase 2: Navigating Backstage
    {
      question: 'O que acontece quando um Developer clica no botão CREATE COMPONENT?',
      options: [
        'Um formulário abre para criar um novo microsserviço',
        'Abre a página de Templates com opções de scaffold',
        'Mostra uma página de erro porque Developers têm acesso read-only',
        'Redireciona para o Azure DevOps para criar um novo repositório',
      ],
      correctIndex: 2,
      explanation:
        "O Step 2 da Fase 2 (Quick actions) diz: 'CREATE COMPONENT: Visível para todos, mas clicar como Developer mostra uma página de erro. Isso é esperado, não é um bug.' Sem saber disso, o dev abre ticket desnecessário.",
    },
    // Q4 — Fase 2: Navigating Backstage
    {
      question: 'Quais são os três Kinds principais que você usa no dia a dia no Catalog?',
      options: [
        'Application, Database e Pipeline',
        'Service, Endpoint e Domain',
        'Component, Resource e System',
        'Repository, Namespace e Cluster',
      ],
      correctIndex: 2,
      explanation:
        'O Step 5 da Fase 2 (Understanding kinds) lista: Component (aplicações: microservices, webapps, libs, frontends), Resource (infra: Storage Accounts, KeyVaults, Redis, CosmosDB) e System (agrupa todos os Components e Resources do projeto). Sem isso, o dev não consegue navegar no Catalog.',
    },
    // Q5 — Fase 2: Navigating Backstage
    {
      question: 'Para que serve o Relations Graph na página do System?',
      options: [
        'Mostra o histórico de deploys do projeto',
        'Exibe o consumo de recursos (CPU/memória) de cada componente',
        'Mostra como tudo no projeto se conecta: quais Components dependem de quais Resources e como os serviços se relacionam',
        'Lista os usuários que têm acesso ao System',
      ],
      correctIndex: 2,
      explanation:
        "O Step 3 da Fase 3 (Relations graph) explica que o card Relations mostra como tudo no projeto se conecta: quais Components dependem de quais Resources e como os serviços se relacionam. Clicar em 'View graph' abre a visualização completa. Essencial para entender dependências e avaliar impacto de mudanças.",
    },
    // Q6 — Fase 3: Finding Your Project
    {
      question: 'Como você encontra o seu projeto no Catalog?',
      options: [
        'Buscar o nome do projeto apenas na Search bar global',
        "Ir ao Catalog, mudar o Kind para 'System', localizar o projeto pelo nome",
        "Clicar em 'Domains' no menu lateral esquerdo",
        'Abrir o ArgoCD e navegar até a aplicação',
      ],
      correctIndex: 1,
      explanation:
        "O Step 1 da Fase 3 (Find your system) diz: 'Para encontrar: vá ao Catalog, mude o Kind para System, localize seu projeto pelo nome ou use o campo de busca, e clique para abrir.' É a primeira coisa que o dev faz na plataforma.",
    },
    // Q7 — Fase 4: External Tools Overview
    {
      question: 'Qual label filter no ArgoCD mostra as aplicações do ambiente de desenvolvimento?',
      options: [
        'env=dev',
        'env=dv',
        'environment=development',
        'stage=dv',
      ],
      correctIndex: 1,
      explanation:
        "O Step 1 da Fase 4 (ArgoCD) lista os filtros de label: 'env=dv para Development, env=ts para Test, env=pr para Production.' O dev usa isso todo dia para encontrar suas aplicações.",
    },
    // Q8 — Fase 4: External Tools Overview
    {
      question: 'Quais são as três etapas do pipeline no Azure DevOps, na ordem correta?',
      options: [
        'Build → Test → Deploy no Kubernetes',
        'Lint → Compile → Push para o ArgoCD',
        'Tests → Build and Push Image → Update GitOps (bom.yaml)',
        'Compile → Package → Deploy via Helm',
      ],
      correctIndex: 2,
      explanation:
        "O Step 3 da Fase 4 (GitHub & Azure DevOps) descreve as três etapas do pipeline: '1. Tests, 2. Build and Push Image, 3. Update GitOps (bom.yaml).' O pipeline atualiza o bom.yaml no repositório GitOps, e o ArgoCD detecta a mudança e faz o auto-deploy. Entender esse fluxo é essencial para debugar falhas de pipeline.",
    },
    // Q9 — Fase 5: Permissions & Security Rules
    {
      question: 'Qual das opções abaixo um Developer PODE fazer no IDP?',
      options: [
        'Criar novos Resources via templates do Backstage',
        'Promover ambientes de dev para produção',
        'Navegar por todo o Catalog e acessar o ArgoCD para ver status de deploy',
        'Gerenciar secrets diretamente no KeyVault',
      ],
      correctIndex: 2,
      explanation:
        "O Step 1 da Fase 5 (Your permissions as developer) mostra na coluna 'You CAN': 'Browse the entire Catalog' e 'Access ArgoCD for deploy status.' As outras opções estão na coluna 'You CANNOT': criar Resources, promover ambientes e gerenciar KeyVault são exclusivos do Tech Lead.",
    },
    // Q10 — Fase 5: Permissions & Security Rules
    {
      question: 'Qual das práticas abaixo é estritamente PROIBIDA segundo as regras de segurança?',
      options: [
        'Usar o modo Builder no Grafana para consultar logs',
        'Fazer push de código para o branch develop',
        'Armazenar código ou dados da Nestlé fora do Azure DevOps/GitHub',
        'Acessar o ArgoCD para verificar status de deploy',
      ],
      correctIndex: 2,
      explanation:
        "O Step 2 da Fase 5 (Security rules) lista como 'Prohibited': 'Code outside Azure DevOps/GitHub.' Também proibido: acesso ao Azure Portal, secrets hardcoded no código, recursos cloud externos e ChatGPT público com dados da Nestlé. Violação gera alertas automáticos e pode resultar em revogação de acesso.",
    },
  ],

  es: [
    // Q1 — Fase 1: Welcome & Platform Overview
    {
      question: '¿Cuáles son los dos repositorios con los que el desarrollador trabaja en el IDP?',
      options: [
        'Repositorio de la aplicación (código) y repositorio GitOps (configuración por entorno)',
        'Repositorio de la aplicación (código) y repositorio de Dockerfiles',
        'Repositorio GitOps (configuración) y repositorio de Helm Chart',
        'Repositorio de Azure DevOps y repositorio de ArgoCD',
      ],
      correctIndex: 0,
      explanation:
        'El Step 3 de la Fase 1 (How it all connects) explica que el dev trabaja con dos repos: el Application repo (código en GitHub) y el GitOps repo (configuración de cómo la aplicación corre en cada entorno: variables, secrets, CPU/memoria). Confundir los dos causa errores de deploy.',
    },
    // Q2 — Fase 1: Welcome & Platform Overview
    {
      question: '¿Quién es responsable de crear y mantener los Dockerfiles?',
      options: [
        'Cada equipo de desarrollo crea su propio Dockerfile',
        'El Tech Lead los crea durante la promoción de entornos',
        'El equipo de Plataforma crea y mantiene todos los Dockerfiles',
        'Los Dockerfiles se generan automáticamente por ArgoCD',
      ],
      correctIndex: 2,
      explanation:
        "El Step 3 de la Fase 1 tiene un callout: 'El Dockerfile NO es responsabilidad del dev, el equipo de Plataforma lo crea y mantiene. Incluso si creas uno, no se usará.' Un dev que crea Dockerfile pierde tiempo porque el pipeline lo ignora.",
    },
    // Q3 — Fase 2: Navigating Backstage
    {
      question: '¿Qué sucede cuando un Developer hace clic en el botón CREATE COMPONENT?',
      options: [
        'Se abre un formulario para crear un nuevo microservicio',
        'Se abre la página de Templates con opciones de scaffold',
        'Muestra una página de error porque los Developers tienen acceso read-only',
        'Redirige a Azure DevOps para crear un nuevo repositorio',
      ],
      correctIndex: 2,
      explanation:
        "El Step 2 de la Fase 2 (Quick actions) dice: 'CREATE COMPONENT: Visible para todos, pero al hacer clic como Developer muestra una página de error. Esto es esperado, no es un bug.' Sin saber esto, el dev abre un ticket innecesario.",
    },
    // Q4 — Fase 2: Navigating Backstage
    {
      question: '¿Cuáles son los tres Kinds principales que usas a diario en el Catalog?',
      options: [
        'Application, Database y Pipeline',
        'Service, Endpoint y Domain',
        'Component, Resource y System',
        'Repository, Namespace y Cluster',
      ],
      correctIndex: 2,
      explanation:
        'El Step 5 de la Fase 2 (Understanding kinds) lista: Component (aplicaciones: microservicios, webapps, libs, frontends), Resource (infra: Storage Accounts, KeyVaults, Redis, CosmosDB) y System (agrupa todos los Components y Resources del proyecto). Sin esto, el dev no puede navegar el Catalog.',
    },
    // Q5 — Fase 2: Navigating Backstage
    {
      question: '¿Para qué sirve el Relations Graph en la página del System?',
      options: [
        'Muestra el historial de deploys del proyecto',
        'Muestra el consumo de recursos (CPU/memoria) de cada componente',
        'Muestra cómo todo en el proyecto se conecta: qué Components dependen de qué Resources y cómo los servicios se relacionan',
        'Lista los usuarios que tienen acceso al System',
      ],
      correctIndex: 2,
      explanation:
        "El Step 3 de la Fase 3 (Relations graph) explica que la tarjeta Relations muestra cómo todo en el proyecto se conecta: qué Components dependen de qué Resources y cómo los servicios se relacionan. Hacer clic en 'View graph' abre la visualización completa. Esencial para entender dependencias y evaluar el impacto de cambios.",
    },
    // Q6 — Fase 3: Finding Your Project
    {
      question: '¿Cómo encuentras tu proyecto en el Catalog?',
      options: [
        'Buscar el nombre del proyecto solo en la Search bar global',
        "Ir al Catalog, cambiar el Kind a 'System', localizar el proyecto por nombre",
        "Hacer clic en 'Domains' en el menú lateral izquierdo",
        'Abrir ArgoCD y navegar hasta la aplicación',
      ],
      correctIndex: 1,
      explanation:
        "El Step 1 de la Fase 3 (Find your system) dice: 'Para encontrarlo: ve al Catalog, cambia el Kind a System, localiza tu proyecto por nombre o usa el campo de búsqueda, y haz clic para abrir.' Es lo primero que el dev hace en la plataforma.",
    },
    // Q7 — Fase 4: External Tools Overview
    {
      question: '¿Qué label filter en ArgoCD muestra las aplicaciones del entorno de desarrollo?',
      options: [
        'env=dev',
        'env=dv',
        'environment=development',
        'stage=dv',
      ],
      correctIndex: 1,
      explanation:
        "El Step 1 de la Fase 4 (ArgoCD) lista los filtros de label: 'env=dv para Development, env=ts para Test, env=pr para Production.' El dev lo usa todos los días para encontrar sus aplicaciones.",
    },
    // Q8 — Fase 4: External Tools Overview
    {
      question: '¿Cuáles son las tres etapas del pipeline en Azure DevOps, en el orden correcto?',
      options: [
        'Build → Test → Deploy en Kubernetes',
        'Lint → Compile → Push a ArgoCD',
        'Tests → Build and Push Image → Update GitOps (bom.yaml)',
        'Compile → Package → Deploy vía Helm',
      ],
      correctIndex: 2,
      explanation:
        "El Step 3 de la Fase 4 (GitHub & Azure DevOps) describe las tres etapas del pipeline: '1. Tests, 2. Build and Push Image, 3. Update GitOps (bom.yaml).' El pipeline actualiza el bom.yaml en el repositorio GitOps, y ArgoCD detecta el cambio y hace el auto-deploy. Entender este flujo es esencial para debuggear fallas de pipeline.",
    },
    // Q9 — Fase 5: Permissions & Security Rules
    {
      question: '¿Cuál de las siguientes opciones PUEDE hacer un Developer en el IDP?',
      options: [
        'Crear nuevos Resources vía templates de Backstage',
        'Promover entornos de dev a producción',
        'Navegar por todo el Catalog y acceder a ArgoCD para ver estado de deploy',
        'Gestionar secrets directamente en KeyVault',
      ],
      correctIndex: 2,
      explanation:
        "El Step 1 de la Fase 5 (Your permissions as developer) muestra en la columna 'You CAN': 'Browse the entire Catalog' y 'Access ArgoCD for deploy status.' Las otras opciones están en la columna 'You CANNOT': crear Resources, promover entornos y gestionar KeyVault son exclusivos del Tech Lead.",
    },
    // Q10 — Fase 5: Permissions & Security Rules
    {
      question: '¿Cuál de las siguientes prácticas es estrictamente PROHIBIDA según las reglas de seguridad?',
      options: [
        'Usar el modo Builder en Grafana para consultar logs',
        'Hacer push de código al branch develop',
        'Almacenar código o datos de Nestlé fuera de Azure DevOps/GitHub',
        'Acceder a ArgoCD para verificar estado de deploy',
      ],
      correctIndex: 2,
      explanation:
        "El Step 2 de la Fase 5 (Security rules) lista como 'Prohibited': 'Code outside Azure DevOps/GitHub.' También prohibido: acceso al Azure Portal, secrets hardcoded en el código, recursos cloud externos y ChatGPT público con datos de Nestlé. La violación genera alertas automáticas y puede resultar en revocación de acceso.",
    },
  ],
};

/* ================================================================
 *  Journey 3 quiz — "When Something Goes Wrong"
 *  7 questions, 90% to pass (6 of 7), unlimited retries
 *  Phase 1: Q1–Q2 (Check Your Logs First)
 *  Phase 2: Q3–Q5 (Check Your Deploy)
 *  Phase 3: Q6–Q7 (When to Ask for Help)
 * ================================================================ */

export const journey3Quiz: QuizByLanguage = {
  en: [
    // Q1 — Phase 1: Check Your Logs First
    {
      question: 'When something goes wrong with your application, what is the first place you should investigate?',
      options: [
        'ArgoCD, to check the deploy status',
        'The pipeline in GitHub Actions, to see if a stage failed',
        'Grafana, to filter the application logs in the correct environment',
        'Open a ticket directly for the Platform Team',
      ],
      correctIndex: 2,
      explanation:
        "Step 1 of Phase 1 (Start with Grafana) is clear: 'When something goes wrong with your application, the first place to investigate is Grafana.' The troubleshooting flow is: Grafana first → ArgoCD if not in the logs → ticket only as a last resort.",
    },
    // Q2 — Phase 1: Check Your Logs First
    {
      question: 'If the problem appears in the Grafana logs, whose responsibility is it to fix?',
      options: [
        'The Platform Team, because they maintain the infrastructure',
        'The Tech Lead, who has edit permissions',
        'The development team itself, in the code or configuration',
        'The Security team, because it could be a compliance issue',
      ],
      correctIndex: 2,
      explanation:
        "Step 2 of Phase 1 (What to look for in logs) has a callout: 'If the problem appears in the logs, it is your team\'s responsibility to fix it in the code or configuration.' Application errors, startup failures, and health check failures are the team's responsibility.",
    },
    // Q3 — Phase 2: Check Your Deploy
    {
      question: 'When should you go to ArgoCD to investigate a problem?',
      options: [
        'Always, before looking at anything else',
        'When the problem does not appear in the Grafana logs and is happening before the pods initialize',
        'Only when the Tech Lead asks',
        'Only when the pipeline fails',
      ],
      correctIndex: 1,
      explanation:
        "Step 3 of Phase 2 (When it is not in the logs) says: 'If the problem is not showing in the Grafana logs and is happening before the pods initialize, go to ArgoCD.' ArgoCD is the second step of the funnel, not the first.",
    },
    // Q4 — Phase 2: Check Your Deploy
    {
      question: 'What is the most common cause of deploy failures?',
      options: [
        'Network issue between ArgoCD and Kubernetes',
        'The Dockerfile being outdated',
        'Misconfiguration in the GitOps repository YAML',
        'Insufficient memory in the Kubernetes cluster',
      ],
      correctIndex: 2,
      explanation:
        "Step 4 of Phase 2 (GitOps misconfiguration) says: 'The most common cause of deploy failures: something is wrong in your GitOps YAML files.' Indentation errors, missing required fields, invalid YAML. The fix: validate with YAML Lint before committing.",
    },
    // Q5 — Phase 2: Check Your Deploy
    {
      question: 'The pipeline ran successfully, but ArgoCD still shows the old version. What should you do?',
      options: [
        'Open a ticket for the Platform Team immediately',
        'Delete the application in ArgoCD and recreate it',
        'Use Hard Refresh in ArgoCD: click the Refresh button and select Hard Refresh',
        'Push a new commit to force the pipeline to run again',
      ],
      correctIndex: 2,
      explanation:
        "Step 6 of Phase 2 (Image not updating) explains: 'Use Hard Refresh: click the Refresh button and select Hard Refresh. This forces ArgoCD to fetch the latest data from the GitOps repository. Sync happens automatically after.'",
    },
    // Q6 — Phase 3: When to Ask for Help
    {
      question: 'At what point should you open a ticket for the Platform Team?',
      options: [
        'As soon as the deploy fails, so you don\'t waste time',
        'When the Tech Lead authorizes opening the ticket',
        'After checking Grafana logs, ArgoCD status, pipeline logs, and GitOps YAML files, and none of those resolved the issue',
        'Only when the problem affects the production environment',
      ],
      correctIndex: 2,
      explanation:
        "Step 7 of Phase 3 (Open a ticket) lists the checklist: '1. Grafana logs for application errors, 2. ArgoCD for Health/Sync status and App Conditions, 3. Pipeline logs for failed stages, 4. GitOps YAML files for misconfigurations.' Only after checking all of these should you open a ticket.",
    },
    // Q7 — Phase 3: When to Ask for Help
    {
      question: 'What should you include when opening a ticket for the Platform Team?',
      options: [
        'Only the application name and the affected environment',
        'Screenshots of the relevant panels (ArgoCD, pipeline, Grafana) with context about what was already investigated',
        'The application source code for the team to analyze',
        'Access to the GitOps repository for the team to edit directly',
      ],
      correctIndex: 1,
      explanation:
        "Step 7 of Phase 3 says: 'Attach screenshots of the relevant panels (ArgoCD status, pipeline logs, Grafana queries) with additional context about what you already investigated. This helps the Platform Team resolve the issue faster.' The more context, the faster the team resolves it.",
    },
  ],

  'pt-BR': [
    // Q1 — Fase 1: Check Your Logs First
    {
      question: 'Quando algo dá errado com sua aplicação, qual é o primeiro lugar que você deve investigar?',
      options: [
        'ArgoCD, para verificar o status do deploy',
        'O pipeline no GitHub Actions, para ver se falhou algum stage',
        'Grafana, para filtrar os logs da aplicação no ambiente correto',
        'Abrir um ticket direto para o time de Plataforma',
      ],
      correctIndex: 2,
      explanation:
        "O Step 1 da Fase 1 (Start with Grafana) é claro: 'When something goes wrong with your application, the first place to investigate is Grafana.' O fluxo de troubleshooting é: Grafana primeiro → ArgoCD se não está nos logs → ticket só como último recurso.",
    },
    // Q2 — Fase 1: Check Your Logs First
    {
      question: 'Se o problema aparece nos logs do Grafana, de quem é a responsabilidade de resolver?',
      options: [
        'Do time de Plataforma, porque eles mantêm a infraestrutura',
        'Do Tech Lead, que tem permissões de edição',
        'Do próprio time de desenvolvimento, no código ou configuração',
        'Do time de Segurança, porque pode ser um problema de compliance',
      ],
      correctIndex: 2,
      explanation:
        "O Step 2 da Fase 1 (What to look for in logs) tem um callout: 'If the problem appears in the logs, it is your team\'s responsibility to fix it in the code or configuration.' Erros de aplicação, falhas de startup e health check failures são responsabilidade do time.",
    },
    // Q3 — Fase 2: Check Your Deploy
    {
      question: 'Quando você deve ir ao ArgoCD para investigar um problema?',
      options: [
        'Sempre, antes de olhar qualquer outra coisa',
        'Quando o problema não aparece nos logs do Grafana e está acontecendo antes dos pods inicializarem',
        'Apenas quando o Tech Lead pedir',
        'Somente quando o pipeline falhar',
      ],
      correctIndex: 1,
      explanation:
        "O Step 3 da Fase 2 (When it is not in the logs) diz: 'If the problem is not showing in the Grafana logs and is happening before the pods initialize, go to ArgoCD.' ArgoCD é o segundo passo do funil, não o primeiro.",
    },
    // Q4 — Fase 2: Check Your Deploy
    {
      question: 'Qual é a causa mais comum de falhas de deploy?',
      options: [
        'Problema de rede entre o ArgoCD e o Kubernetes',
        'O Dockerfile estar desatualizado',
        'Erro de configuração no YAML do repositório GitOps',
        'Falta de memória no cluster Kubernetes',
      ],
      correctIndex: 2,
      explanation:
        "O Step 4 da Fase 2 (Erros de configuração GitOps) diz: 'The most common cause of deploy failures: something is wrong in your GitOps YAML files.' Erros de indentação, campos obrigatórios faltando, YAML inválido. A solução: validar com YAML Lint antes de commitar.",
    },
    // Q5 — Fase 2: Check Your Deploy
    {
      question: 'O pipeline rodou com sucesso, mas o ArgoCD ainda mostra a versão antiga. O que você deve fazer?',
      options: [
        'Abrir um ticket para o time de Plataforma imediatamente',
        'Deletar a aplicação no ArgoCD e recriá-la',
        'Usar Hard Refresh no ArgoCD: clicar em Refresh e selecionar Hard Refresh',
        'Fazer um novo push para forçar o pipeline a rodar de novo',
      ],
      correctIndex: 2,
      explanation:
        "O Step 6 da Fase 2 (Image not updating) explica: 'Use Hard Refresh: click the Refresh button and select Hard Refresh. This forces ArgoCD to fetch the latest data from the GitOps repository. Sync happens automatically after.'",
    },
    // Q6 — Fase 3: When to Ask for Help
    {
      question: 'Em que momento você deve abrir um ticket para o time de Plataforma?',
      options: [
        'Assim que o deploy falhar, para não perder tempo',
        'Quando o Tech Lead autorizar a abertura do ticket',
        'Após verificar logs no Grafana, status no ArgoCD, logs do pipeline e arquivos YAML do GitOps, e nenhum desses resolver',
        'Apenas quando o problema afetar o ambiente de produção',
      ],
      correctIndex: 2,
      explanation:
        "O Step 7 da Fase 3 (Open a ticket) lista o checklist: '1. Grafana logs for application errors, 2. ArgoCD for Health/Sync status and App Conditions, 3. Pipeline logs for failed stages, 4. GitOps YAML files for misconfigurations.' Só depois de verificar tudo isso é que se abre ticket.",
    },
    // Q7 — Fase 3: When to Ask for Help
    {
      question: 'O que você deve incluir ao abrir um ticket para o time de Plataforma?',
      options: [
        'Apenas o nome da aplicação e o ambiente afetado',
        'Screenshots dos painéis relevantes (ArgoCD, pipeline, Grafana) com contexto do que já foi investigado',
        'O código-fonte da aplicação para o time analisar',
        'Acesso ao repositório GitOps para o time editar diretamente',
      ],
      correctIndex: 1,
      explanation:
        "O Step 7 da Fase 3 diz: 'Attach screenshots of the relevant panels (ArgoCD status, pipeline logs, Grafana queries) with additional context about what you already investigated. This helps the Platform Team resolve the issue faster.' Quanto mais contexto, mais rápido o time resolve.",
    },
  ],

  es: [
    // Q1 — Fase 1: Check Your Logs First
    {
      question: 'Cuando algo sale mal con tu aplicación, ¿cuál es el primer lugar que debes investigar?',
      options: [
        'ArgoCD, para verificar el estado del deploy',
        'El pipeline en GitHub Actions, para ver si falló alguna etapa',
        'Grafana, para filtrar los logs de la aplicación en el entorno correcto',
        'Abrir un ticket directamente para el equipo de Plataforma',
      ],
      correctIndex: 2,
      explanation:
        "El Step 1 de la Fase 1 (Start with Grafana) es claro: 'When something goes wrong with your application, the first place to investigate is Grafana.' El flujo de troubleshooting es: Grafana primero → ArgoCD si no está en los logs → ticket solo como último recurso.",
    },
    // Q2 — Fase 1: Check Your Logs First
    {
      question: 'Si el problema aparece en los logs de Grafana, ¿de quién es la responsabilidad de resolverlo?',
      options: [
        'Del equipo de Plataforma, porque mantienen la infraestructura',
        'Del Tech Lead, que tiene permisos de edición',
        'Del propio equipo de desarrollo, en el código o configuración',
        'Del equipo de Seguridad, porque podría ser un problema de compliance',
      ],
      correctIndex: 2,
      explanation:
        "El Step 2 de la Fase 1 (What to look for in logs) tiene un callout: 'If the problem appears in the logs, it is your team\'s responsibility to fix it in the code or configuration.' Errores de aplicación, fallos de startup y health check failures son responsabilidad del equipo.",
    },
    // Q3 — Fase 2: Check Your Deploy
    {
      question: '¿Cuándo debes ir a ArgoCD para investigar un problema?',
      options: [
        'Siempre, antes de mirar cualquier otra cosa',
        'Cuando el problema no aparece en los logs de Grafana y está ocurriendo antes de que los pods se inicialicen',
        'Solo cuando el Tech Lead lo pida',
        'Solo cuando el pipeline falle',
      ],
      correctIndex: 1,
      explanation:
        "El Step 3 de la Fase 2 (When it is not in the logs) dice: 'If the problem is not showing in the Grafana logs and is happening before the pods initialize, go to ArgoCD.' ArgoCD es el segundo paso del embudo, no el primero.",
    },
    // Q4 — Fase 2: Check Your Deploy
    {
      question: '¿Cuál es la causa más común de fallas de deploy?',
      options: [
        'Problema de red entre ArgoCD y Kubernetes',
        'El Dockerfile estar desactualizado',
        'Mala configuración en el YAML del repositorio GitOps',
        'Falta de memoria en el cluster Kubernetes',
      ],
      correctIndex: 2,
      explanation:
        "El Step 4 de la Fase 2 (Mala configuración en GitOps) dice: 'The most common cause of deploy failures: something is wrong in your GitOps YAML files.' Errores de indentación, campos obligatorios faltantes, YAML inválido. La solución: validar con YAML Lint antes de commitear.",
    },
    // Q5 — Fase 2: Check Your Deploy
    {
      question: 'El pipeline corrió con éxito, pero ArgoCD aún muestra la versión antigua. ¿Qué debes hacer?',
      options: [
        'Abrir un ticket para el equipo de Plataforma inmediatamente',
        'Eliminar la aplicación en ArgoCD y recrearla',
        'Usar Hard Refresh en ArgoCD: hacer clic en Refresh y seleccionar Hard Refresh',
        'Hacer un nuevo push para forzar al pipeline a correr de nuevo',
      ],
      correctIndex: 2,
      explanation:
        "El Step 6 de la Fase 2 (Image not updating) explica: 'Use Hard Refresh: click the Refresh button and select Hard Refresh. This forces ArgoCD to fetch the latest data from the GitOps repository. Sync happens automatically after.'",
    },
    // Q6 — Fase 3: When to Ask for Help
    {
      question: '¿En qué momento debes abrir un ticket para el equipo de Plataforma?',
      options: [
        'Tan pronto como el deploy falle, para no perder tiempo',
        'Cuando el Tech Lead autorice la apertura del ticket',
        'Después de verificar logs en Grafana, estado en ArgoCD, logs del pipeline y archivos YAML de GitOps, y ninguno de esos resolver el problema',
        'Solo cuando el problema afecte el entorno de producción',
      ],
      correctIndex: 2,
      explanation:
        "El Step 7 de la Fase 3 (Open a ticket) lista el checklist: '1. Grafana logs for application errors, 2. ArgoCD for Health/Sync status and App Conditions, 3. Pipeline logs for failed stages, 4. GitOps YAML files for misconfigurations.' Solo después de verificar todo eso se debe abrir ticket.",
    },
    // Q7 — Fase 3: When to Ask for Help
    {
      question: '¿Qué debes incluir al abrir un ticket para el equipo de Plataforma?',
      options: [
        'Solo el nombre de la aplicación y el entorno afectado',
        'Screenshots de los paneles relevantes (ArgoCD, pipeline, Grafana) con contexto de lo que ya fue investigado',
        'El código fuente de la aplicación para que el equipo lo analice',
        'Acceso al repositorio GitOps para que el equipo edite directamente',
      ],
      correctIndex: 1,
      explanation:
        "El Step 7 de la Fase 3 dice: 'Attach screenshots of the relevant panels (ArgoCD status, pipeline logs, Grafana queries) with additional context about what you already investigated. This helps the Platform Team resolve the issue faster.' Cuanto más contexto, más rápido el equipo lo resuelve.",
    },
  ],
};
